import type { GetSsoConnectionsResponse } from '@/legacy-api/workos-auth-slice.gen'

import { toError } from '@moment/observability/logger'
import { useCallback, useMemo } from 'react'

import { Routes } from '@/app/routes'
import { workosAuthApi } from '@/legacy-api/workos-auth-slice.gen'
import { createLogger } from '@/lib/logger'

const LOGGER = createLogger('app/auth/_lib/use-auth-api')

interface PasswordAuthRequest {
  email: string
  password: string
}

interface PasswordAuthResponse {
  success: boolean
  requiresMagicAuth?: boolean | null
  redirectTo?: string
  message?: string
  error?: string
}

interface MagicAuthSendRequest {
  email: string
}

interface MagicAuthSendResponse {
  success: boolean
  message?: string
  error?: string
}

interface MagicAuthVerifyRequest {
  email: string
  code: string
}

interface MagicAuthVerifyResponse {
  success: boolean
  redirectTo?: string
  authMethod?: 'magic_auth' | 'password_fallback'
  magicAuthDisabled?: boolean | null
  error?: string
}

interface PasswordResetSendRequest {
  email: string
  resetUrl: string
}

interface PasswordResetSendResponse {
  success: boolean
  error?: string
}

interface PasswordResetRequest {
  newPassword: string
  token: string
}

interface PasswordResetResponse {
  success: boolean
  message?: string
  error?: string
}

interface SsoConnectionsRequest {
  domain: string
}

type SsoConnectionsResponse = GetSsoConnectionsResponse & {
  error?: string
}

interface SsoInitiateRequest {
  organization: string
  returnUrl: string
}

interface SsoInitiateResponse {
  authorizationUrl?: string
  error?: string
}

interface LogoutResponse {
  success: boolean
  message?: string
  error?: string
}

/**
 * Extracts error message from RTK Query error response
 * Provides consistent error handling across all auth methods
 * Handles multiple error formats to avoid swallowing unexpected errors
 */
function extractRtkError(error: unknown): string {
  // Check if it's an object
  if (error && typeof error === 'object') {
    // RTK Query error format: { data?: { error?: string } }
    if ('data' in error && error.data && typeof error.data === 'object') {
      if ('error' in error.data && typeof error.data.error === 'string') {
        return error.data.error
      }
    }
    // Standard Error object: { message?: string }
    if ('message' in error && typeof error.message === 'string') {
      return error.message
    }
  }
  // Fallback for unexpected error formats
  return 'An error occurred'
}

/**
 * Sets authentication cookie via Next.js API route
 * This is needed when the backend returns the token in the response body
 * instead of setting it directly as a cookie (to avoid SameSite issues)
 * Always sets httpOnly=true for security when using backend API
 */
async function setAuthCookieViaNextJS(
  accessToken: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const setCookieResponse = await fetch(
      `${window.location.origin}/api/auth/set-cookie`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      },
    )

    if (!setCookieResponse.ok) {
      let errorMessage = 'Failed to set authentication cookie'
      try {
        const errorData = await setCookieResponse.json()
        errorMessage = errorData.error || errorMessage
      } catch {
        // Response wasn't JSON - use default message
      }
      return {
        success: false,
        error: errorMessage,
      }
    }

    return { success: true }
  } catch {
    return {
      success: false,
      error: 'Failed to set authentication cookie',
    }
  }
}

export function useAuthApi() {
  const [passwordAuthMutation, { reset: resetPasswordAuth }] =
    workosAuthApi.usePasswordAuthMutation()
  const [magicAuthSendMutation] = workosAuthApi.useMagicAuthSendMutation()
  const [magicAuthVerifyMutation, { reset: resetMagicAuthVerify }] =
    workosAuthApi.useMagicAuthVerifyMutation()
  const [sendPasswordResetMutation] =
    workosAuthApi.useSendPasswordResetMutation()
  const [resetPasswordMutation] = workosAuthApi.useResetPasswordMutation()
  const [ssoConnectionsLazy] = workosAuthApi.useLazyGetSsoConnectionsQuery()
  const [ssoInitiateLazy] = workosAuthApi.useLazySsoInitiateQuery()

  const passwordAuth = useCallback(
    async (request: PasswordAuthRequest): Promise<PasswordAuthResponse> => {
      const result = await passwordAuthMutation({
        passwordAuthRequest: request,
      })
      if ('error' in result) {
        return {
          success: false,
          error: extractRtkError(result.error) || 'Authentication failed',
        }
      }

      // Backend returned token in response body, now set it as same-origin cookie
      if (result.data.accessToken) {
        const cookieResult = await setAuthCookieViaNextJS(
          result.data.accessToken,
        )
        if (!cookieResult.success) {
          return {
            success: false,
            error: cookieResult.error,
          }
        }
      }

      // Evict the RTK cache entry so the accessToken doesn't linger in JS memory.
      // The point of httpOnly cookies is to keep the token out of JS; holding it
      // in the Redux store would undermine that goal.
      const { accessToken: _pw, ...passwordAuthData } = result.data
      resetPasswordAuth()
      return passwordAuthData
    },
    [passwordAuthMutation, resetPasswordAuth],
  )

  const sendMagicAuthCode = useCallback(
    async (request: MagicAuthSendRequest): Promise<MagicAuthSendResponse> => {
      const result = await magicAuthSendMutation({
        magicAuthSendRequest: request,
      })
      if ('error' in result) {
        return {
          success: false,
          error: extractRtkError(result.error) || 'Failed to send code',
        }
      }
      return result.data
    },
    [magicAuthSendMutation],
  )

  const verifyMagicAuth = useCallback(
    async (
      request: MagicAuthVerifyRequest,
    ): Promise<MagicAuthVerifyResponse> => {
      const result = await magicAuthVerifyMutation({
        magicAuthVerifyRequest: request,
      })
      if ('error' in result) {
        return {
          success: false,
          error: extractRtkError(result.error) || 'Verification failed',
        }
      }

      // Backend returned token in response body, now set it as same-origin cookie
      if (result.data.accessToken) {
        const cookieResult = await setAuthCookieViaNextJS(
          result.data.accessToken,
        )
        if (!cookieResult.success) {
          return {
            success: false,
            error: cookieResult.error,
          }
        }
      }

      // Evict the RTK cache entry so the accessToken doesn't linger in JS memory.
      // The point of httpOnly cookies is to keep the token out of JS; holding it
      // in the Redux store would undermine that goal.
      const { accessToken: _mav, ...magicAuthVerifyData } = result.data
      resetMagicAuthVerify()
      return magicAuthVerifyData
    },
    [magicAuthVerifyMutation, resetMagicAuthVerify],
  )

  const sendPasswordReset = useCallback(
    async (
      request: PasswordResetSendRequest,
    ): Promise<PasswordResetSendResponse> => {
      const result = await sendPasswordResetMutation({
        sendPasswordResetRequest: request,
      })
      if ('error' in result) {
        return {
          success: false,
          error: extractRtkError(result.error) || 'Failed to send reset email',
        }
      }
      return result.data
    },
    [sendPasswordResetMutation],
  )

  const resetPassword = useCallback(
    async (request: PasswordResetRequest): Promise<PasswordResetResponse> => {
      const result = await resetPasswordMutation({
        resetPasswordRequest: request,
      })
      if ('error' in result) {
        return {
          success: false,
          error: extractRtkError(result.error) || 'Failed to reset password',
        }
      }
      return result.data
    },
    [resetPasswordMutation],
  )

  const getSsoConnections = useCallback(
    async (request: SsoConnectionsRequest): Promise<SsoConnectionsResponse> => {
      const result = await ssoConnectionsLazy(request)
      if ('error' in result) {
        return {
          connectionTypes: [],
          error:
            extractRtkError(result.error) || 'Failed to fetch SSO connections',
        }
      }
      return result.data || { connectionTypes: [] }
    },
    [ssoConnectionsLazy],
  )

  const getSsoAuthorizationUrl = useCallback(
    async (request: SsoInitiateRequest): Promise<SsoInitiateResponse> => {
      const result = await ssoInitiateLazy(request)
      if ('error' in result) {
        return {
          error: extractRtkError(result.error) || 'Failed to get SSO URL',
        }
      }
      return result.data || {}
    },
    [ssoInitiateLazy],
  )

  const logout = useCallback(async (): Promise<LogoutResponse> => {
    // Clear the frontend HttpOnly cookie via Next.js API route
    // This is necessary because the cookie is HttpOnly and can't be cleared by client JS
    try {
      const response = await fetch(
        `${window.location.origin}${Routes.Auth.logout}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        },
      )

      if (!response.ok) {
        LOGGER.error('Failed to clear auth cookie', {
          error: new Error('Failed to clear auth cookie'),
          httpStatus: response.status,
        })
        return {
          success: false,
          error: 'Failed to clear authentication cookie',
        }
      }

      return { success: true }
    } catch (err) {
      LOGGER.error('Error during logout', { error: toError(err) })
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Logout failed',
      }
    }
  }, [])

  // Memoize return object to prevent creating new references on every render
  // This prevents useEffect dependencies from triggering unnecessarily
  return useMemo(
    () => ({
      passwordAuth,
      sendMagicAuthCode,
      verifyMagicAuth,
      sendPasswordReset,
      resetPassword,
      getSsoConnections,
      getSsoAuthorizationUrl,
      logout,
    }),
    [
      passwordAuth,
      sendMagicAuthCode,
      verifyMagicAuth,
      sendPasswordReset,
      resetPassword,
      getSsoConnections,
      getSsoAuthorizationUrl,
      logout,
    ],
  )
}
